<template>
  <v-card class="mb-4" :class="!isMobile?'pa-3':''" flat>
    <!-- Desktop -->
    <v-row v-if="!isMobile" align="center" dense>
      <span v-if="displayRange" class="ml-4 text-caption">
        <v-icon size="14" class="mr-1">mdi-calendar</v-icon>
        {{ displayRange }}
      </span>

      <v-spacer />

      <datePresets
        :presets="presets"
        :activePreset="activePreset"
        @selectPreset="selectPreset"
      />

      <!-- CustomRange dentro de un menú (desktop) -->
      <v-menu
        v-model="menuDesktop"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
      >
        <template #activator="{ props }">
          <v-btn class="ml-2" color="primary" v-bind="props" variant="text" size="small">
            <v-icon size="18" class="mr-1">mdi-calendar-range</v-icon>
            Rango personalizado
          </v-btn>
        </template>

        <customDateRange
          v-model:modelValue="pickerRange"
          :minDate="minDate"
          :maxDate="maxDate"
          @applyCustomRange="() => { applyCustomRange(); menuDesktop = false; }"
          @cancel="menuDesktop = false"
        />
      </v-menu>
    </v-row>

    <!-- Mobile / Tablet -->
    <v-row v-else align="center" dense class="ma-0">
      <v-menu
        v-model="mainMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        location="bottom start"
        :offset="8"
      >
        <template #activator="{ props }">
          <v-btn
            block
            v-bind="props"
            variant="tonal"
            size="small"
            class="py-5"
            aria-label="Abrir filtros de fecha"
          >
            <v-icon size="18" class="mr-1">mdi-calendar-range</v-icon>
            {{ displayRange || 'Selecciona un rango' }}
          </v-btn>
        </template>

        <v-card class="pa-2 text-center" min-width="320" max-width="92vw">
          <datePresets
            :presets="presets"
            :activePreset="activePreset"
            @selectPreset="p => { selectPreset(p); if (p.label === 'Todos los tiempos') mainMenu = false; }"
          />

          <v-divider class="my-2" />

          <!-- Última opción: botón que despliega el mismo CustomRange (no abierto por default) -->
          <v-btn variant="text" size="small" class="ml-2" @click="showInlinePicker = !showInlinePicker">
            <v-icon size="18" class="mr-1">mdi-calendar-range</v-icon>
            Rango personalizado
          </v-btn>

          <v-expand-transition>
            <div v-if="showInlinePicker" class="mt-2">
              <customDateRange
                v-model:modelValue="pickerRange"
                :minDate="minDate"
                :maxDate="maxDate"
                @applyCustomRange="() => { applyCustomRange(); showInlinePicker = false; mainMenu = false; }"
                @cancel="showInlinePicker = false"
              />
            </div>
          </v-expand-transition>
        </v-card>
      </v-menu>
    </v-row>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

import datePresets from '@/components/filters/date/Presets.vue';
import customDateRange from '@/components/filters/date/CustomRange.vue';
import { useDisplay } from 'vuetify';

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

const emit = defineEmits<{
  (e: 'rangeUpdated', payload: { start: Date; end: Date } | null): void
}>();

// Helpers
const TZ = 'America/Monterrey';
const toISO = (d: Date) => d.toLocaleString('sv-SE', { timeZone: TZ }).slice(0, 10);

// State
const menuDesktop = ref(false);
const mainMenu = ref(false);
const showInlinePicker = ref(false);
const pickerRange = ref<Date[] | null>(null);

const minDate = ref('2020-01-01');
const maxDate = ref(toISO(new Date()));

// Presets (devuelven Date[] | null)
const presets = [
  { label: 'Hoy', range: () => { const t = new Date(); return [t, t] as Date[]; } },
  { label: 'Ayer', range: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [d, d] as Date[]; } },
  { label: 'Últimos 7 días', range: () => { const today = new Date(); const weekAgo = new Date(); weekAgo.setDate(today.getDate() - 6); return [weekAgo, today] as Date[]; } },
  { label: 'Este mes', range: () => { const t = new Date(); const first = new Date(t.getFullYear(), t.getMonth(), 1); const last = new Date(t.getFullYear(), t.getMonth() + 1, 0); return [first, last] as Date[]; } },
  { label: 'Mes pasado', range: () => { const t = new Date(); const first = new Date(t.getFullYear(), t.getMonth() - 1, 1); const last = new Date(t.getFullYear(), t.getMonth(), 0); return [first, last] as Date[]; } },
  { label: 'Todos los tiempos', range: () => null },
] as const;

const activePreset = ref<string>(presets[2].label); // 'Últimos 7 días'

// UI text
const displayRange = computed(() => {
  if (activePreset.value === 'Todos los tiempos') return 'Todos los tiempos';
  if (!pickerRange.value || pickerRange.value.length === 0) return '';
  const sorted = [...pickerRange.value].sort((a, b) => a.getTime() - b.getTime());
  const start = toISO(sorted[0]);
  const end = toISO(sorted[sorted.length - 1]);
  return start === end ? start : `${start} - ${end}`;
});

// Orquestación
function emitRange(range: Date[] | null) {
  if (!range) { emit('rangeUpdated', null); return; }
  const sorted = [...range].sort((a, b) => a.getTime() - b.getTime());
  const start = new Date(sorted[0]);
  const end = new Date(sorted[sorted.length - 1]);
  emit('rangeUpdated', { start, end });
}

function selectPreset(preset: { label: string; range: () => Date[] | null }) {
  const range = preset.range();
  pickerRange.value = range;
  activePreset.value = preset.label;
  emitRange(range);
}

function applyCustomRange() {
  if (!pickerRange.value || pickerRange.value.length === 0) {
    activePreset.value = 'Todos los tiempos';
    emitRange(null);
    return;
  }
  const sorted = [...pickerRange.value].sort((a, b) => a.getTime() - b.getTime());
  const start = sorted[0];
  const end = sorted[sorted.length - 1];
  activePreset.value = ''; // limpias el preset al usar custom
  emitRange([start, end]);
}

onMounted(() => {
  selectPreset(presets[2]); // 'Últimos 7 días'
});
</script>
